/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { Button, CircularProgressBar, Dropdown, Paragraph, TextField, Title } from '@cambridgeassessment/cambridge-ui';
import { useDispatch, useSelector } from 'react-redux';
import {
  Assessment,
  QualificationMin,
  Series,
  EntryComponent,
  BUSINESS_UNITS,
  DownloadFileTypes,
} from '@cambridgeassessment/lib-ee-commons';
import { RootState } from '../../../../store';
import { ButtonContainer, ProgressContainer, StyledInputContainer } from './WorkDownloading.styles';
import * as seriesThunks from '../../../../store/series/thunks';
import Loader from '../../../../components/Loader/Loader';
import DownloadTable from './DownloadTable';
import * as sectionsThunks from '../../../../store/sections/thunks';
import qs from 'qs';
import { useHistory, useLocation } from 'react-router-dom';
import actions from '../../../../store/sections/actions';
import { pickBy } from 'lodash';
import { SectionForSelector } from '../../../../types/Sections';
import { onDataChanged } from '../utils';
import { sortSectionDropdown } from '../utils/helper';

const MAX_CENTRE_ID_LENGTH = 5;

const pastTwoYears = new Date();
pastTwoYears.setFullYear(pastTwoYears.getFullYear() - 2);

interface WorkDownloadFields {
  assessment?: boolean;
  component?: boolean;
  qualification?: boolean;
  section?: boolean;
}

interface QueryParams {
  seriesId?: string;
  qualificationId?: string;
  assessmentCode?: string;
  componentNumber?: string;
  centreId?: string;
  learnerNumber?: string;
  sectionId?: string;
  isComponentDocument?: boolean;
}

interface InputsState {
  series: Series | null;
  qualification: QualificationMin | null;
  assessment: Assessment | null;
  component: EntryComponent | null;
  section: SectionForSelector | null;
  centreId: string;
  learnerNumber: string;
  isComponentDocument: boolean;
}

const renderSeries = (item: Series): string => item.name;
const renderQualifications = (item: QualificationMin): string => item.name;
const renderAssessments = (item: Assessment): string => item.code;
const renderComponents = (item: EntryComponent): string => item.number;
const renderSections = (item: SectionForSelector): string => item.name;

const initialInputsState = {
  series: null,
  qualification: null,
  assessment: null,
  component: null,
  section: null,
  centreId: '',
  learnerNumber: '',
  isComponentDocument: false,
};

const Selector = (): React.ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const {
    userData: { businessUnit },
    series: { isLoading: isSeriesLoading, seriesData, hasError },
    sections: { qualifications, assessments, components, sections, fileResults, filesTotal, areFilesLoading },
  } = useSelector((state: RootState) => state);
  const [queryParams, setQueryParams] = useState<QueryParams>();
  const [inputs, setInputs] = useState<InputsState>({ ...initialInputsState });
  const [currentData, setCurrentData] = useState<InputsState>({ ...initialInputsState });
  const [errors, setErrors] = useState<{ centreId?: string; learnerNumber?: string }>({});
  const [sectionList, setSectionList] = useState<SectionForSelector[]>([]);

  const twoYearsSeries = seriesData?.filter((series) => new Date(series.endDate) > pastTwoYears) || [];

  const titleByBusinessId = businessUnit?.id === BUSINESS_UNITS.CIE.id ? 'syllabus' : 'specification';

  useEffect(() => {
    const params: QueryParams = qs.parse(location.search, { ignoreQueryPrefix: true });

    setQueryParams(params);
  }, [location.search]);

  useEffect(() => {
    if (!businessUnit) return;

    dispatch(seriesThunks.getSeriesData(businessUnit.id));
  }, [businessUnit, dispatch]);

  useEffect(() => {
    const list: SectionForSelector[] = sections.map((section) => ({
      name: section.name,
      value: section.id?.toString(),
      isComponentDocument: section.learnerNumber === null,
    }));

    list.push({
      name: 'Supporting material',
      value: DownloadFileTypes.Supporting,
      isComponentDocument: false,
    });

    setSectionList(sortSectionDropdown(list));
  }, [sections]);

  useEffect(() => {
    onDataChanged<Series>(queryParams?.seriesId, twoYearsSeries, inputs.series, 'id', (series) =>
      onSeriesSelected(series, false),
    );
    onDataChanged<QualificationMin>(
      queryParams?.qualificationId,
      qualifications,
      inputs.qualification,
      'id',
      (qualification) => onQualificationSelected(qualification, false),
    );
    onDataChanged<Assessment>(queryParams?.assessmentCode, assessments, inputs.assessment, 'code', (assessment) =>
      onAssessmentSelected(assessment, false),
    );
    onDataChanged<EntryComponent>(queryParams?.componentNumber, components, inputs.component, 'number', (component) =>
      onComponentSelected(component, false),
    );
    onDataChanged<SectionForSelector>(queryParams?.sectionId, sectionList, inputs.section, 'value', (section) =>
      onSectionSelected(section),
    );

    if (queryParams?.centreId && !inputs.centreId) {
      onCentreChange(queryParams.centreId);
    }

    if (queryParams?.learnerNumber && !inputs.learnerNumber) {
      onLearnerChange(queryParams.learnerNumber);
    }
  }, [
    queryParams,
    seriesData,
    inputs.series,
    qualifications,
    inputs.qualification,
    assessments,
    inputs.assessment,
    components,
    inputs.component,
    sectionList,
    inputs.section,
    dispatch,
  ]);

  const onSeriesSelected = (series: Series, resetState = true): void => {
    if (inputs.series?.id == series.id) return;

    setInputs((prevState) => ({ ...prevState, series }));

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, seriesId: series.id.toString() }));

    if (!businessUnit) return;

    dispatch(seriesThunks.getDetailedSeries(businessUnit.id, series.id));
    dispatch(sectionsThunks.getQualifications(businessUnit.id, series.id));

    if (!resetState) return;

    resetFields({ assessment: true, component: true, qualification: true, section: true });
  };

  const onQualificationSelected = (qualification: QualificationMin, resetState = true): void => {
    if (inputs.qualification?.id == qualification.id || !businessUnit || !inputs.series) return;

    setInputs((prevState) => ({ ...prevState, qualification }));
    dispatch(sectionsThunks.getAssessments(businessUnit.id, inputs.series.id, qualification.id));

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, qualificationId: qualification.id.toString() }));

    if (!resetState) return;

    resetFields({ assessment: true, component: true, section: true });
  };

  const onAssessmentSelected = (assessment: Assessment, resetState = true): void => {
    if (inputs.assessment?.code === assessment.code || !businessUnit || !inputs.series || !inputs.qualification) return;

    setInputs((prevState) => ({ ...prevState, assessment }));
    dispatch(sectionsThunks.getComponents(businessUnit.id, inputs.series.id, inputs.qualification.id, assessment.code));

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, assessmentCode: assessment.code }));

    if (!resetState) return;

    resetFields({ component: true, section: true });
  };

  const onComponentSelected = (component: EntryComponent, resetState = true): void => {
    if (
      inputs.component?.number === component.number ||
      !businessUnit ||
      !inputs.series ||
      !inputs.qualification ||
      !inputs.assessment
    )
      return;

    setInputs((prevState) => ({ ...prevState, component }));
    dispatch(
      sectionsThunks.getSections(
        businessUnit.id,
        inputs.series.id,
        inputs.qualification.id,
        inputs.assessment.code,
        component.number,
      ),
    );

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, componentNumber: component.number }));

    if (!resetState) return;

    resetFields({ section: true });
  };

  const onCentreChange = (value: string): void => {
    const lettersNumbersOnly = /^[A-Za-z0-9]*$/;
    const matched = lettersNumbersOnly.exec(value);

    setErrors((prevErrors) => ({ ...prevErrors, centreId: undefined }));

    if (!matched) {
      setErrors((prevErrors) => ({ ...prevErrors, centreId: 'Centre number must contain letters and numbers only' }));
    } else if (value.length > MAX_CENTRE_ID_LENGTH) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        centreId: `Centre number cannot exceed ${MAX_CENTRE_ID_LENGTH} characters`,
      }));
    }

    setInputs((prevState) => ({ ...prevState, centreId: value }));
    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, centreId: value }));
  };

  const onSectionSelected = (section: SectionForSelector): void => {
    dispatch(actions.setFileResults(undefined));
    setInputs((prevState) => ({ ...prevState, section }));
    setQueryParams((prevQueryParams) => ({
      ...prevQueryParams,
      sectionId: section.value,
      isComponentDocument: section.isComponentDocument,
    }));
  };

  const onLearnerChange = (value: string): void => {
    const numbersOnly = /^[-,0-9 ]*$/;
    const matched = numbersOnly.exec(value);

    setErrors((prevErrors) => ({ ...prevErrors, learnerNumber: undefined }));

    if (!matched) {
      setErrors((prevErrors) => ({ ...prevErrors, learnerNumber: 'Learner number must contain numbers only' }));
    }

    setInputs((prevState) => ({ ...prevState, learnerNumber: value }));
    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, learnerNumber: value }));
  };

  const onWorkSearch = (): void => {
    fetchFiles(true);
    buildQueryParams();
    setCurrentData(inputs);
  };

  const buildQueryParams = useCallback(() => {
    const filteredQueryParams = pickBy(queryParams, (v) => v !== undefined);
    const search = qs.stringify(filteredQueryParams);

    history.push({ search });
  }, [queryParams, history]);

  const fetchFiles = useCallback(
    (reset?: boolean, resolver?: () => void): void => {
      if (!businessUnit || !inputs.series || !inputs.qualification || !inputs.assessment || !inputs.component) {
        return;
      }

      dispatch(
        sectionsThunks.getTableResults(
          businessUnit.id,
          {
            seriesId: inputs.series.id,
            qualificationId: inputs.qualification.id,
            assessmentCode: inputs.assessment.code,
            componentNumber: inputs.component.number,
            sectionId: inputs.section?.value,
            centreId: inputs.centreId.toUpperCase(),
            learnerNumber: inputs.learnerNumber,
            isComponentDocument: String(inputs.section?.isComponentDocument),
          },
          reset,
          resolver,
        ),
      );
    },
    [
      businessUnit,
      inputs.series,
      inputs.qualification,
      inputs.assessment,
      inputs.component,
      inputs.section,
      inputs.centreId,
      inputs.learnerNumber,
      inputs.isComponentDocument,
      dispatch,
    ],
  );

  const loadMoreItems = (): Promise<void> => new Promise((resolve) => fetchFiles(false, resolve));

  const resetFields = ({ qualification, assessment, component, section }: WorkDownloadFields): void => {
    const clearParams: QueryParams = {};

    if (qualification) {
      setInputs((prevState) => ({ ...prevState, qualification: null }));
      clearParams.qualificationId = undefined;
    }
    if (assessment) {
      setInputs((prevState) => ({ ...prevState, assessment: null }));
      clearParams.assessmentCode = undefined;
    }
    if (component) {
      setInputs((prevState) => ({ ...prevState, component: null }));
      clearParams.componentNumber = undefined;
    }
    if (section) {
      setInputs((prevState) => ({ ...prevState, section: null }));
      clearParams.sectionId = undefined;
    }

    setQueryParams((prevQueryParams) => ({ ...prevQueryParams, ...clearParams }));
    dispatch(actions.setFileResults(undefined));
  };

  const isValidForm = useCallback(
    (): boolean =>
      !!inputs.series &&
      !!inputs.qualification &&
      !!inputs.assessment &&
      !!inputs.component &&
      !!inputs.section &&
      !!inputs.centreId &&
      !errors.centreId &&
      !errors.learnerNumber,
    [
      inputs.series,
      inputs.qualification,
      inputs.assessment,
      inputs.component,
      inputs.section,
      inputs.centreId,
      errors.centreId,
      errors.learnerNumber,
    ],
  );

  if (isSeriesLoading && !inputs.series) return <Loader />;

  if (hasError) return <div>Failed to get Series data</div>;

  return (
    <>
      <Title id='series'>Select series</Title>
      <StyledInputContainer>
        <Dropdown<Series>
          data={twoYearsSeries}
          renderOption={renderSeries}
          renderSelectedOption={renderSeries}
          dataQaId='series-select'
          onChange={onSeriesSelected}
          value={inputs.series}
          placeholder='Series'
          title='Select series'
        />
      </StyledInputContainer>
      <Title id='qualification'>Select qualification</Title>
      <StyledInputContainer>
        <Dropdown<QualificationMin>
          data={qualifications}
          renderOption={renderQualifications}
          renderSelectedOption={renderQualifications}
          dataQaId='qualification-select'
          onChange={onQualificationSelected}
          value={inputs.qualification}
          disabled={!inputs.series}
          placeholder='Qualification'
          title='Select qualification'
        />
      </StyledInputContainer>
      <Title id='syllabus'>Select {titleByBusinessId}</Title>
      <StyledInputContainer>
        <Dropdown<Assessment>
          data={assessments}
          renderOption={renderAssessments}
          renderSelectedOption={renderAssessments}
          dataQaId='syllabus-select'
          onChange={onAssessmentSelected}
          value={inputs.assessment}
          disabled={!inputs.qualification}
          placeholder={titleByBusinessId[0].toUpperCase() + titleByBusinessId.slice(1)}
          title='Select syllabus/specification'
        />
      </StyledInputContainer>
      <Title id='component'>Select component</Title>
      <StyledInputContainer>
        <Dropdown<EntryComponent>
          data={components}
          renderOption={renderComponents}
          renderSelectedOption={renderComponents}
          dataQaId='component-select'
          onChange={onComponentSelected}
          value={inputs.component}
          disabled={!inputs.assessment}
          placeholder='Component'
          title='Select component'
        />
      </StyledInputContainer>
      <Paragraph data-qa-id='component-document-explanation'>
        Component documents can be searched for by selecting them as a section from the section dropdown.
      </Paragraph>
      <Title id='section'>Section</Title>
      <StyledInputContainer>
        <Dropdown<SectionForSelector>
          data={sectionList}
          renderOption={renderSections}
          renderSelectedOption={renderSections}
          dataQaId='section-select'
          onChange={onSectionSelected}
          value={inputs.section}
          disabled={!inputs.component}
          placeholder='Please select learner section'
          title='Select learner section'
        />
      </StyledInputContainer>
      <Title>Add centre</Title>
      <StyledInputContainer>
        <TextField
          dataQaId='search-centre'
          onChange={onCentreChange}
          placeholder='Centre number'
          value={inputs.centreId}
          errorMsg={errors.centreId}
          disabled={!inputs.section}
        />
        <Paragraph data-qa-id='explanation-on-multiple-choice'>
          Optionally add one or more candidate. If you want to add multiple candidates, separate then by commas.
          Example: 2, 5, 10
        </Paragraph>
        <Title>Add candidate</Title>
        <TextField
          dataQaId='search-candidate'
          onChange={onLearnerChange}
          placeholder='Candidate number'
          helperText='Optional'
          value={inputs.learnerNumber}
          errorMsg={errors.learnerNumber}
          disabled={inputs.section?.isComponentDocument}
        />
      </StyledInputContainer>
      <ButtonContainer>
        <Button disabled={!isValidForm()} onClick={onWorkSearch} qaId='search-button'>
          Search
        </Button>
      </ButtonContainer>
      {fileResults && businessUnit && (
        <DownloadTable
          loadMoreItems={loadMoreItems}
          businessUnitId={businessUnit?.id}
          seriesId={currentData.series?.id}
          assessmentCode={currentData.assessment?.code}
          componentNumber={currentData.component?.number}
          qualificationId={currentData.qualification?.id}
          sectionId={currentData.section?.value}
          centreId={currentData.centreId.toUpperCase()}
          learnerNumber={currentData.learnerNumber}
          seriesName={currentData.series?.name}
          filesTotal={filesTotal}
          titleByBU={titleByBusinessId}
        />
      )}
      {areFilesLoading && !fileResults && (
        <div>
          <ProgressContainer>
            <CircularProgressBar variant='indeterminate' size={44} />
          </ProgressContainer>
          <Paragraph>Fetching candidate work. Please wait</Paragraph>
        </div>
      )}
      {areFilesLoading && fileResults && <Loader />}
    </>
  );
};

export default Selector;
